import { KeyboardEvent, useId, useState } from 'react';
import { fieldValue } from './polaris';
import './Combobox.css';

export type ComboboxOption = { label: string; value: string };

type Props = {
    label: string;
    value: string;
    options: ComboboxOption[];
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    emptyMessage?: string;
    /** `immediate` is true for a picked or cleared value, false while typing. */
    onChange: (value: string, immediate: boolean) => void;
};

const matchingOptions = (options: ComboboxOption[], value: string) => {
    const query = value.trim().toLowerCase();
    const usable = options.filter(option => option.value);
    return query
        ? usable.filter(option => option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query))
        : usable;
};

/**
 * Text field with suggestions that also accepts new values. Polaris web
 * components have no combobox, so the field is native and only the suggestion
 * list is custom, styled to match the admin popover.
 */
export default function Combobox({
    label, value, options, disabled, loading, error, placeholder,
    emptyMessage = 'No saved values. Type a new value to use it.',
    onChange,
}: Props) {
    const listId = useId();
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const suggestions = matchingOptions(options, value);
    const showList = open && !disabled;

    const choose = (next: string) => {
        onChange(next, true);
        setOpen(false);
        setActive(-1);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive(current => suggestions.length === 0 ? -1 : (current + step + suggestions.length) % suggestions.length);
        } else if (event.key === 'Enter' && showList && active >= 0 && suggestions[active]) {
            event.preventDefault();
            choose(suggestions[active].value);
        } else if (event.key === 'Escape' && showList) {
            event.preventDefault();
            setOpen(false);
        }
    };

    return (
        <div
            className="pf-combobox"
            onKeyDown={onKeyDown}
            onFocus={() => setOpen(true)}
            onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
            }}
        >
            <s-text-field
                label={label}
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                error={error}
                autocomplete="off"
                onInput={event => {
                    onChange(fieldValue(event), false);
                    setOpen(true);
                    setActive(-1);
                }}
            >
                {value && !disabled && (
                    <s-button slot="accessory" variant="tertiary" icon="x" accessibilityLabel={`Clear ${label}`}
                        onClick={() => choose('')} />
                )}
            </s-text-field>
            {showList && (
                <div className="pf-combobox__list" role="listbox" id={listId} aria-label={label}>
                    {loading && suggestions.length === 0 ? (
                        <div className="pf-combobox__message"><s-spinner size="base" accessibilityLabel={`Loading ${label}`} /></div>
                    ) : suggestions.length === 0 ? (
                        <div className="pf-combobox__message"><s-text color="subdued">{emptyMessage}</s-text></div>
                    ) : suggestions.map((option, index) => (
                        <div
                            key={option.value}
                            role="option"
                            aria-selected={option.value === value}
                            className={`pf-combobox__option${index === active ? ' pf-combobox__option--active' : ''}`}
                            // Keep focus in the field so blur does not close the list first.
                            onMouseDown={event => event.preventDefault()}
                            onMouseEnter={() => setActive(index)}
                            onClick={() => choose(option.value)}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
